import { Platform } from 'react-native';
import { prepareReturnToAppLinks } from '@/lib/auth/prepare-return-to-app-link';
import { buildNativeAppDeepLink } from '@/lib/auth/native-app-deep-link';
import { USER_ROLE } from '@/types/user-role';
import { hasClientProfile, hasProviderProfile, isProviderRole } from '@/utils/user-role';

/** Query na URL web aberta pelo app nativo (cadastro fora do app). */
export const RETURN_TO_APP_PARAM = 'return_to_app';

const SESSION_KEY = 'controlapag_return_to_app';
const LOCAL_KEY = 'controlapag_return_to_app';

function isTruthyReturnParam(value: string | null): boolean {
  return value === '1' || value === 'true';
}

export function appendReturnToAppParam(url: string): string {
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has(RETURN_TO_APP_PARAM)) {
      parsed.searchParams.set(RETURN_TO_APP_PARAM, '1');
    }
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${RETURN_TO_APP_PARAM}=1`;
  }
}

export function markWebReturnToAppFlow(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_KEY, '1');
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCAL_KEY, '1');
  }
}

/** Persiste flag no browser (SPA perde query ao trocar de rota). */
export function captureReturnToAppFromUrl(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const value = new URLSearchParams(window.location.search).get(RETURN_TO_APP_PARAM);
  if (isTruthyReturnParam(value)) {
    markWebReturnToAppFlow();
  }
}

export function isWebReturnToAppFlow(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1') {
    return true;
  }
  if (typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_KEY) === '1') {
    return true;
  }
  return false;
}

export function clearWebReturnToAppFlow(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEY);
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(LOCAL_KEY);
  }
}

export function getNativeLoginAfterWebOnboardingUrl(): string {
  return buildNativeAppDeepLink('login', { registered: '1' });
}

/** Cliente cadastrado na web após abrir o site pelo app nativo — volta direto ao app. */
export function shouldWebReturnToAppAfterClientRegistration(user: {
  role: string;
  clientProfile?: unknown;
  providerProfile?: unknown;
}): boolean {
  if (!isWebReturnToAppFlow()) return false;
  if (isProviderRole(user.role as USER_ROLE) && hasProviderProfile(user)) {
    return false;
  }
  return user.role === USER_ROLE.CLIENT || hasClientProfile(user);
}

/** Fallback se o link <a> não estiver pronto; prefira href pré-montado na tela. */
export async function openNativeAppFromWeb(): Promise<
  'opened' | 'fallback_login' | 'no_session'
> {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return 'no_session';
  }

  const { openUrl } = await prepareReturnToAppLinks();
  if (!openUrl) {
    return 'no_session';
  }

  clearWebReturnToAppFlow();
  window.location.href = openUrl;
  return openUrl.includes('handoff=') ? 'opened' : 'fallback_login';
}
